import { tryFetch, HttpMethod, type HttpResponse } from '@/modules/services/http'
import type { ActivationErrorCode, RegisterErrorCode, UserErrorCode } from '../types/user.enums'
import type {
  User,
  UserSetting,
  RegisterUser,
  UserProfile,
  ActivationUser,
} from '../models/user.model'
import type { ProfileRequest } from './user.requests'

const BASE_URL = import.meta.env.VITE_MAIN_BASE_URL

const getCurrentUser = async (): Promise<HttpResponse<User | null, UserErrorCode>> =>
  await tryFetch<User | null, UserErrorCode>(HttpMethod.Get, BASE_URL, ['users', 'me'])

const getUserProfile = async (): Promise<HttpResponse<UserProfile | null, UserErrorCode>> =>
  await tryFetch<UserProfile | null, UserErrorCode>(HttpMethod.Get, BASE_URL, [
    'users',
    'me',
    'profile',
  ])

const getUserSettings = async (
  clientKey: string,
  userId?: number,
): Promise<HttpResponse<UserSetting, UserErrorCode>> =>
  await tryFetch<UserSetting, UserErrorCode>(HttpMethod.Get, BASE_URL, [
    'clients',
    clientKey,
    'users',
    userId ?? 'me',
    'settings',
  ])

const getUserActivation = async (
  userId?: number,
): Promise<HttpResponse<ActivationUser | null, UserErrorCode>> =>
  await tryFetch<ActivationUser | null, UserErrorCode>(HttpMethod.Get, BASE_URL, [
    'users',
    userId ?? 'me',
    'activation',
  ])

const activateCurrentUser = async (
  code: string,
): Promise<HttpResponse<null, ActivationErrorCode>> =>
  await tryFetch<null, ActivationErrorCode>(
    HttpMethod.Post,
    BASE_URL,
    ['users', 'me', 'activate'],
    {
      body: code,
    },
  )

const registerCurrentUser = async (
  body: RegisterUser,
): Promise<HttpResponse<null, RegisterErrorCode>> =>
  await tryFetch<null, RegisterErrorCode>(HttpMethod.Post, BASE_URL, ['users', 'me', 'register'], {
    body,
  })

const updateCurrentUserProfile = async (
  body: ProfileRequest,
): Promise<HttpResponse<null, UserErrorCode>> =>
  await tryFetch<null, UserErrorCode>(HttpMethod.Patch, BASE_URL, ['users', 'me', 'profile'], {
    body,
  })

const updateUserSettings = async (
  clientKey: string,
  userSetting: UserSetting,
  userId?: number,
): Promise<HttpResponse<null, UserErrorCode>> =>
  await tryFetch<null, UserErrorCode>(
    HttpMethod.Patch,
    BASE_URL,
    ['clients', clientKey, 'users', userId ?? 'me', 'settings'],
    {
      body: userSetting,
    },
  )

export const userApi = {
  getCurrentUser,
  getUserProfile,
  getUserSettings,
  getUserActivation,
  activateCurrentUser,
  registerCurrentUser,
  updateCurrentUserProfile,
  updateUserSettings,
}
